import { useEffect, useMemo } from "react";
import { useSelector } from "store/useSelector";
import type * as types from "../../../types/types";
import Timeline from "./Timeline";
import { findGaps, findIntersecting } from "../../../utils/segments";
import {
  useSegmentsForProcess,
  useFocusRange,
  useIsSingleProcessPage,
} from "utils/customHooks/genericHooks";
import RetrotagPopper from "./RetrotagPopper/RetrotagPopper";
import { Portal } from "@mui/material";
import { atom, useAtom } from "jotai";

interface ConnectedTimelineProps {
  process: types.Process;
  offset?: number;
  timelineIdx: number;
  isSkeletonLoader?: boolean;
  style?: React.CSSProperties;
  width: number;
}

const ConnectedTimeline = ({
  offset,
  process,
  timelineIdx,
  isSkeletonLoader = false,
  style = {},
  width,
}: ConnectedTimelineProps) => {
  const isSingleProcessPage = useIsSingleProcessPage();
  const focusRange = useFocusRange();
  const visibleRange = useSelector(state => state.legacy.visibleRange);
  const processSegments = useSegmentsForProcess(process.id);
  const processSegmentsDisplay = useMemo(
    () => (isSkeletonLoader ? [] : processSegments),
    [isSkeletonLoader, processSegments]
  );
  const isReady = processSegments && visibleRange && focusRange;

  // memoize this expensive calculation - only recalculate when [visibleRange, isReady, processSegments] change
  const filledSegments = useMemo(
    () =>
      getFilledSegments(visibleRange, Boolean(isReady), processSegmentsDisplay),
    [visibleRange, isReady, processSegmentsDisplay]
  );

  useDetectTimelineSegmentsLoaded(timelineIdx, filledSegments);

  return isReady ? (
    <>
      {!isSingleProcessPage && (
        <Portal container={() => document.getElementById("timelineStyles")}>
          <RetrotagPopper process={process} />
        </Portal>
      )}
      <Timeline
        style={style}
        process={process}
        productionSegments={filledSegments}
        offset={offset || 0}
        timelineIdx={timelineIdx}
        width={width}
      />
    </>
  ) : null;
};

export default ConnectedTimeline;

/** for each timeline, track whether it's completely loaded */
export const allTimelinesLoadedAtom = atom<{
  [timelineIdx: number]: boolean;
}>({});

const MAX_TIME_TO_WAIT = 10 * 1000;
/** track whether the timeline is completely loaded, store in global state (allTimelinesLoadedAtom) */
function useDetectTimelineSegmentsLoaded(
  timelineIdx: number,
  filledSegments: types.ProductionSegmentType[]
) {
  const visibleRange = useSelector(state => state.legacy.visibleRange);
  const [, setAllTimelinesLoaded] = useAtom(allTimelinesLoadedAtom);
  useEffect(() => {
    const firstSegment = filledSegments[0];
    const lastSegment = filledSegments[filledSegments.length - 1];

    // 2. if all segments are loaded, set the value to true
    const allLoaded =
      filledSegments &&
      filledSegments.length > 0 &&
      filledSegments.every(segment => segment.pnl.category !== "loading") &&
      visibleRange &&
      // first segment must start before focus range
      firstSegment?.range.ge &&
      Number(firstSegment.range.ge) <= visibleRange.ge &&
      // last segment must end after focus range (or null if it's the latest segment)
      (!lastSegment.range.lt ||
        Number(lastSegment.range.lt) >= visibleRange.lt);

    // Set loaded state immediately if segments are already loaded
    if (allLoaded) {
      setAllTimelinesLoaded(prev => ({
        ...prev,
        [timelineIdx]: true,
      }));
      return;
    }

    // Set up a timer to mark as loaded after 10 seconds regardless of actual state
    const timer = setTimeout(() => {
      setAllTimelinesLoaded(prev => ({
        ...prev,
        [timelineIdx]: true,
      }));
    }, MAX_TIME_TO_WAIT);

    // Cleanup function to clear the timer if component unmounts or dependencies change
    return () => {
      clearTimeout(timer);
    };
    // don't need to check again when a different timeline changes, so disable exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filledSegments]);
}

function getFilledSegments(
  visibleRange: types.TimeRangeFinite | null,
  isReady: boolean,
  processSegments: types.ProductionSegmentType[] | null
): types.ProductionSegmentType[] {
  const { ge, lt } = visibleRange || { ge: 0, lt: 0 };
  const now = Date.now();
  const visibleSegments = isReady
    ? findIntersecting(processSegments || [], ge, lt)
    : [];

  // Add segments in all the gaps so we can style those
  // segments in some way to indicate they should be loading.
  const gapSegments = isReady
    ? findGaps(visibleSegments, ge, lt)
        .filter(r => r.ge <= now)
        .map(r => ({
          pnl: { category: "loading" },
          range: { ge: r.ge, lt: Math.min(r.lt, now) },
        }))
    : [];

  return isReady
    ? [...visibleSegments, ...gapSegments].sort((a, b) => {
        if (a.range.ge && b.range.ge) {
          return a.range.ge - b.range.ge;
        } else if (a.range.ge === b.range.ge) {
          return 0;
        } else if (a.range.ge === null) {
          // A null here suggests that the ge is at the
          // beginning of time (a very small number). So
          // a - b would be even smaller (so choose -1).
          return -1;
        }
        return 1;
      })
    : [];
}
